import {
  Download,
  Share,
  Plus,
  X,
  Monitor,
  MoreVertical,
  FileText,
} from 'lucide-react';
import { useT } from '@/lib/i18n';
import { useInstallPrompt, InstallPlatform } from '@/lib/useInstallPrompt';
import { motion, AnimatePresence } from 'motion/react';

function IosSteps() {
  const t = useT();
  return (
    <ol className="space-y-3 text-sm">
      <li className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Share className="h-4 w-4" />
        </span>
        <span>{t('install.ios.step1')}</span>
      </li>
      <li className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Plus className="h-4 w-4" />
        </span>
        <span>{t('install.ios.step2')}</span>
      </li>
    </ol>
  );
}

function SafariMacSteps() {
  const t = useT();
  return (
    <ol className="space-y-3 text-sm">
      <li className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <FileText className="h-4 w-4" />
        </span>
        <span>{t('install.safari.step1')}</span>
      </li>
      <li className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Plus className="h-4 w-4" />
        </span>
        <span>{t('install.safari.step2')}</span>
      </li>
    </ol>
  );
}

function ChromiumSteps() {
  const t = useT();
  return (
    <ol className="space-y-3 text-sm">
      <li className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <MoreVertical className="h-4 w-4" />
        </span>
        <span>{t('install.chrome.step1')}</span>
      </li>
      <li className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Monitor className="h-4 w-4" />
        </span>
        <span>{t('install.chrome.step2')}</span>
      </li>
    </ol>
  );
}

export function InstallBanner() {
  const t = useT();
  const { platform, canShow, install, dismiss, showGuide, closeGuide } =
    useInstallPrompt();

  if (!canShow) return null;

  if (platform === InstallPlatform.FIREFOX) {
    return (
      <div className="mx-3 mb-2 rounded-lg border bg-muted/50 p-2.5 text-[11px] text-muted-foreground">
        <p>{t('install.firefox')}</p>
        <button
          onClick={dismiss}
          className="mt-1.5 text-[10px] font-medium text-primary hover:underline"
        >
          {t('install.dismiss')}
        </button>
      </div>
    );
  }

  const guideTitle =
    platform === InstallPlatform.IOS
      ? t('install.ios.title')
      : platform === InstallPlatform.SAFARI_MAC
        ? t('install.safari.title')
        : t('install.chrome.title');

  return (
    <>
      <div className="mx-3 mb-2">
        <button
          onClick={install}
          className="flex w-full items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 text-[12px] font-semibold text-primary transition-colors hover:bg-primary/10"
        >
          <Download className="h-4 w-4 shrink-0" />
          {t('install.button')}
        </button>
        <button
          onClick={dismiss}
          className="mt-1 w-full text-center text-[10px] text-muted-foreground hover:text-foreground transition-colors"
        >
          {t('install.dismiss')}
        </button>
      </div>

      <AnimatePresence>
        {showGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={closeGuide}
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-sm rounded-2xl bg-card border shadow-lg p-5"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold">{guideTitle}</h3>
                <button
                  onClick={closeGuide}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {platform === InstallPlatform.IOS ? (
                <IosSteps />
              ) : platform === InstallPlatform.SAFARI_MAC ? (
                <SafariMacSteps />
              ) : (
                <ChromiumSteps />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
